import { useCallback, useEffect, useState } from 'react'
import Stack from '@mui/joy/Stack'
import Box from '@mui/joy/Box'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import IconButton from '@mui/joy/IconButton'
import Tooltip from '@mui/joy/Tooltip'
import CircularProgress from '@mui/joy/CircularProgress'
import Alert from '@mui/joy/Alert'
import RefreshIcon from '@mui/icons-material/Refresh'
import ScheduleIcon from '@mui/icons-material/Schedule'
import PaidIcon from '@mui/icons-material/Paid'
import CloudIcon from '@mui/icons-material/Cloud'

import { adminCost, type CostRollup } from '../../api/dsGmeetApi'

export function CostTab() {
    const [data, setData] = useState<CostRollup | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            setData(await adminCost())
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    return (
        <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack gap={0.25}>
                    <Typography level="title-sm">Cost rollup</Typography>
                    <Typography level="body-xs" color="neutral">
                        Month-to-date Recall hours, estimated spend, and GCS storage footprint.
                    </Typography>
                </Stack>
                <Tooltip title="Refresh">
                    <IconButton size="sm" variant="plain" onClick={load} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            {error && <Alert color="danger">{error}</Alert>}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 1.5,
                }}
            >
                <Metric
                    icon={<ScheduleIcon />}
                    label="Hours recorded"
                    value={data ? data.hoursThisMonth.toFixed(1) : '—'}
                    unit="h"
                    loading={loading && !data}
                />
                <Metric
                    icon={<PaidIcon />}
                    label="Estimated Recall spend"
                    value={data ? `$${data.estimatedRecallSpendUsd.toFixed(2)}` : '—'}
                    loading={loading && !data}
                />
                <Metric icon={<CloudIcon />} label="GCS storage" value={data ? data.gcsStorageGb.toFixed(2) : '—'} unit="GB" loading={loading && !data} />
            </Box>

            <Typography level="body-xs" color="neutral">
                Figures refresh on demand. Hours derive from master-transcript durations; spend uses the per-hour rate configured in the service env.
            </Typography>
        </Stack>
    )
}

function Metric({ icon, label, value, unit, loading }: { icon: React.ReactNode; label: string; value: string; unit?: string; loading?: boolean }) {
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1, color: 'text.tertiary' }}>
                {icon}
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {label}
                </Typography>
            </Stack>
            {loading ? (
                <CircularProgress size="sm" />
            ) : (
                <Typography level="h2" sx={{ lineHeight: 1.1 }}>
                    {value}
                    {unit && (
                        <Typography level="body-sm" component="span" sx={{ ml: 0.5, color: 'text.tertiary' }}>
                            {unit}
                        </Typography>
                    )}
                </Typography>
            )}
        </Sheet>
    )
}
